import clsx from "clsx";
import {
  ComponentProps,
  CSSProperties,
  HTMLAttributes,
  PropsWithChildren,
} from "react";

type ColumnWidth = NonNullable<CSSProperties["width"]>;

export type TableColumn = {
  id: string;
  width: ColumnWidth;
  minWidth?: ColumnWidth;
};

const cssLength = (value: ColumnWidth) =>
  typeof value === "number" ? `${value}px` : value;

export const tableMinWidth = (columns: readonly TableColumn[]) =>
  `calc(${columns
    .map(({ minWidth, width }) => cssLength(minWidth ?? width))
    .join(" + ")})`;

export const TableColumns = ({
  columns,
}: {
  columns: readonly TableColumn[];
}) => (
  <colgroup>
    {columns.map(({ id, width }) => (
      <col key={id} style={{ width }} />
    ))}
  </colgroup>
);

export const TableScrollArea = ({
  children,
  className,
  minTableWidth,
  stickyFirstColumn = false,
  stickyLastColumn = false,
  style,
  ...props
}: PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    minTableWidth?: ColumnWidth;
    stickyFirstColumn?: boolean;
    stickyLastColumn?: boolean;
  }
>) => (
  <div
    {...props}
    style={
      {
        ...style,
        "--table-min-width": minTableWidth
          ? cssLength(minTableWidth)
          : undefined,
      } as CSSProperties
    }
    className={clsx(
      "table-scroll-area",
      {
        "table-sticky--left": stickyFirstColumn,
        "table-sticky--right": stickyLastColumn,
      },
      className
    )}>
    {children}
  </div>
);

/** Shared frame; financial data and row rendering stay with each feature. */
export const TableFrame = ({
  columns,
  tableClassName,
  children,
  ...props
}: Omit<ComponentProps<typeof TableScrollArea>, "minTableWidth"> & {
  columns: readonly TableColumn[];
  tableClassName?: string;
}) => (
  <TableScrollArea {...props} minTableWidth={tableMinWidth(columns)}>
    <table className={clsx("table table--fixed", tableClassName)}>
      <TableColumns columns={columns} />
      {children}
    </table>
  </TableScrollArea>
);
